import { useState, useCallback } from 'react';

/**
 * Authentication flow states
 */
export type AuthFlowState =
  | 'idle'
  | 'initiating'
  | 'redirecting'
  | 'processing'
  | 'error';

/**
 * Authentication flow state interface
 */
export interface AuthFlowStateData {
  state: AuthFlowState;
  error?: string;
  progress?: number;
}

/**
 * Authentication flow actions interface
 */
export interface AuthFlowActions {
  startAuth: () => void;
  setRedirecting: () => void;
  setProcessing: () => void;
  setError: (error: string) => void;
  reset: () => void;
}

/**
 * Authentication flow hook return type
 */
export type UseAuthenticationFlowReturn = [AuthFlowStateData, AuthFlowActions];

/**
 * Custom hook for managing authentication flow state
 *
 * This hook provides state management for authentication flows including:
 * - idle: Initial state, ready to start authentication
 * - initiating: Starting authentication process
 * - redirecting: Redirecting to authentication provider
 * - processing: Processing authentication response
 * - error: Authentication error occurred
 *
 * @param initialState - Initial authentication flow state (default: 'idle')
 * @returns Tuple containing [state, actions]
 */
export const useAuthenticationFlow = (
  initialState: AuthFlowState = 'idle',
): UseAuthenticationFlowReturn => {
  const [flowState, setFlowState] = useState<AuthFlowStateData>({
    state: initialState,
  });

  /**
   * Start the authentication process
   */
  const startAuth = useCallback(() => {
    setFlowState({ state: 'initiating' });
  }, []);

  /**
   * Set state to redirecting (user being redirected to auth provider)
   */
  const setRedirecting = useCallback(() => {
    setFlowState({ state: 'redirecting' });
  }, []);

  /**
   * Set state to processing (handling auth response)
   */
  const setProcessing = useCallback(() => {
    setFlowState({ state: 'processing' });
  }, []);

  /**
   * Set error state with error message
   */
  const setError = useCallback((error: string) => {
    setFlowState({ state: 'error', error });
  }, []);

  /**
   * Reset to idle state
   */
  const reset = useCallback(() => {
    setFlowState({ state: 'idle' });
  }, []);

  const actions: AuthFlowActions = {
    startAuth,
    setRedirecting,
    setProcessing,
    setError,
    reset,
  };

  return [flowState, actions];
};

export default useAuthenticationFlow;
